/**
 * Renderer compartido entre el envío real (`notificarHandover`) y el
 * preview del editor de plantillas (`EditarPlantillaDialog`). Garantiza
 * que lo que el admin ve en el preview es EXACTAMENTE lo que sale por
 * correo.
 *
 * Si el admin proporcionó override de `asunto` o `cuerpoTpl` en la
 * `ConfiguracionNotificacion`, se hace sustitución de variables tipo
 * Handlebars-light (`{{var}}`). Si no, se cae al template registrado
 * en `core/mail/templates`.
 */

import { getTemplate } from "./templates";
import type { MailContext, MailRendered } from "./mailContext";
import { escapeHtml } from "@/lib/html";
import { etiquetaTipo } from "@constants/etiquetasTipo";

/**
 * Variables expuestas en los templates. El admin las puede usar tanto
 * en el asunto como en el cuerpo con `{{nombre}}` o `{{path.con.puntos}}`.
 */
export function variablesDisponibles(ctx: MailContext): Record<string, string> {
  return {
    "entidad.tipo": etiquetaTipo(ctx.entidad.tipo),
    "entidad.codigo": ctx.entidad.codigo,
    "entidad.nombre": ctx.entidad.nombre ?? "",
    "actor.email": ctx.actor.email,
    "actor.nombre": ctx.actor.nombre ?? ctx.actor.email,
    "actor.rol": ctx.actor.rol ?? "",
    fecha: new Date(ctx.fecha).toLocaleString("es-CO"),
    motivo: ctx.motivo ?? "",
    url: ctx.url,
    "totales.montoUsd":
      ctx.totales?.montoUsd?.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }) ?? "",
    "totales.montoCop":
      ctx.totales?.montoCop?.toLocaleString("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0,
      }) ?? "",
    "totales.cantidad": ctx.totales?.cantidad?.toString() ?? "",
  };
}

/**
 * Lista descriptiva de variables — para mostrar en la UI como ayuda.
 * El admin la ve junto al editor para saber qué placeholders puede usar.
 */
export const CATALOGO_VARIABLES: ReadonlyArray<{
  nombre: string;
  descripcion: string;
}> = [
  { nombre: "{{entidad.tipo}}", descripcion: "Tipo de entidad (PR, PO, OT, Acta, …)" },
  { nombre: "{{entidad.codigo}}", descripcion: "Código humano de la entidad" },
  { nombre: "{{entidad.nombre}}", descripcion: "Nombre/descripción corta" },
  { nombre: "{{actor.email}}", descripcion: "Correo del usuario que disparó la acción" },
  { nombre: "{{actor.nombre}}", descripcion: "Nombre del usuario" },
  { nombre: "{{actor.rol}}", descripcion: "Rol del usuario" },
  { nombre: "{{fecha}}", descripcion: "Fecha-hora del evento" },
  { nombre: "{{motivo}}", descripcion: "Motivo / observación (rechazos, cancelaciones)" },
  { nombre: "{{url}}", descripcion: "Link directo a la entidad en el PMIS" },
  { nombre: "{{totales.montoUsd}}", descripcion: "Monto en USD si aplica" },
  { nombre: "{{totales.montoCop}}", descripcion: "Monto en COP si aplica" },
  { nombre: "{{totales.cantidad}}", descripcion: "Cantidad si aplica" },
];

/**
 * Renderiza un correo combinando el template default con el override
 * opcional del admin. Es la función que usan TANTO `notificarHandover`
 * (envío real) COMO el preview del editor.
 */
export function renderizarMail(
  handoverId: string,
  ctx: MailContext,
  asuntoOverride?: string,
  cuerpoOverride?: string,
): MailRendered {
  const def = getTemplate(handoverId)(ctx);
  const vars = variablesDisponibles(ctx);
  // El asunto es texto plano → interpolación cruda. El cuerpo va como HTML por
  // Graph (contentType HTML) → se ESCAPAN los valores de las variables para que
  // un nombre/motivo con `<a href>`/markup no se inyecte en el correo enviado.
  const subject = asuntoOverride ? interpolar(asuntoOverride, vars) : def.subject;
  const html = cuerpoOverride ? interpolarHtml(cuerpoOverride, vars) : def.html;
  return { subject, html };
}

/**
 * Sustituye `{{var}}` por su valor en `vars`. Si la variable no existe,
 * la deja vacía (en lugar de dejar el placeholder visible). Versión cruda:
 * úsese solo para texto plano (asunto). Para cuerpos HTML, usar `interpolarHtml`.
 */
export function interpolar(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, k: string) => vars[k] ?? "");
}

/**
 * Igual que `interpolar`, pero ESCAPA el valor de cada variable para insertarlo
 * con seguridad en un cuerpo HTML. El markup de la plantilla (lo que el admin
 * escribe) queda literal; solo los VALORES de `{{var}}` —datos de usuario/
 * entidad, no confiables— se neutralizan. Evita inyección de marcado/enlaces en
 * el correo real enviado por Graph (el preview in-app ya va en iframe sandbox).
 */
export function interpolarHtml(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, k: string) => escapeHtml(vars[k] ?? ""));
}

/**
 * Construye un MailContext de ejemplo para el preview. Datos plausibles
 * pero ficticios — solo para que el admin vea cómo queda el correo
 * antes de guardar.
 */
export function ctxEjemplo(
  entidadTipo: MailContext["entidad"]["tipo"] = "PR",
): MailContext {
  return {
    actor: {
      email: "carlos.galvis@tecpetrol.com",
      nombre: "Carlos Galvis",
      rol: "JEFE",
    },
    entidad: {
      tipo: entidadTipo,
      codigo: `${etiquetaTipo(entidadTipo)}-2026-014`,
      id: "00000000-0000-0000-0000-000000000001",
      nombre: "Suministro tubería 6\" — Pozo CPO-13 #38",
    },
    url: "https://tecpetrol.sharepoint.com/sites/CPO-13/PMIS/#/pr/00000000",
    fecha: new Date().toISOString(),
    totales: {
      montoUsd: 48750,
      montoCop: 195_000_000,
    },
  };
}
